import { DeepNullablePartial, ErrorCode, Reference } from '@approvalmax/types';
import { toast } from '@approvalmax/ui/src/components';
import { compareHelpers, errorHelpers, mathService, numberHelpers } from '@approvalmax/utils';
import { useMutation } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import uniqueId from 'lodash/uniqueId';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import moment from 'moment';
import { useEditRequestForm } from 'pages/requestForm/hooks/useEditRequestForm';
import { getEditCommands } from 'pages/requestForm/selectors/requestSelectors';
import { useCallback, useMemo } from 'react';
import { amplitudeService } from 'services/amplitude';
import { notificationService } from 'services/notification';
import { useCompleteReview, useGetRequest, usePublishRequest } from 'shared/data/webApp/v1';
import { GetNetSuiteBillPaymentBills } from 'shared/data/webApp/v2';
import { useNetSuiteAttachmentsFieldSettings, useNetSuiteBillPaymentFieldSettings } from 'shared/hooks';
import {
    hasChangesState,
    netSuiteBillPaymentApplyListState,
    NetSuiteBillPaymentFields,
    netSuiteBillPaymentFieldValuesState,
} from 'shared/states';

import { getCustomFieldsTransfer, setCurrencyPrecision } from '../helpers';
import { useChangeNetSuiteRequest, useCurrencyPrecision } from '../hooks';
import { isFutureDate } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteCustomFieldsState, netSuiteShowErrorsState } from '../resources/states';
import {
    getBillPaymentValidationErrors,
    mapBillPaymentBillAttachment,
    redirectToRequest,
} from './NetSuiteBillPaymentRequestForm.helpers';
import { messages } from './NetSuiteBillPaymentRequestForm.messages';
import {
    netSuiteBillPaymentApplyListRequestIdsWithPaymentErrorState,
    netSuiteBillPaymentApplyListRequestIdsWithStatusErrorState,
    netSuiteBillPaymentBillsFilterTransferState,
    netSuiteBillPaymentCreditListState,
    netSuiteBillPaymentSelectableBillsState,
} from './NetSuiteBillPaymentRequestForm.states';

export const useNetSuiteBillPaymentAmount = (currency: Reference | null | undefined) => {
    const applyList = useAtomValue(netSuiteBillPaymentApplyListState);

    const currencyPrecision = useCurrencyPrecision(currency);

    return useMemo(
        () =>
            mathService.round(
                applyList.reduce((total, item) => mathService.add(total, item.paymentAmount || 0), 0),
                currencyPrecision
            ),
        [applyList, currencyPrecision]
    );
};

export const useNetSuiteBillPaymentClearItems = () => {
    const setApplyList = useSetAtom(netSuiteBillPaymentApplyListState);
    const setCreditList = useSetAtom(netSuiteBillPaymentCreditListState);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const clearItems = useCallback(() => {
        setApplyList([]);
        setCreditList([]);

        changeNetSuiteRequest();
    }, [changeNetSuiteRequest, setApplyList, setCreditList]);

    return clearItems;
};

export const useNetSuiteBillApplyListChanges = () => {
    const setApplyList = useSetAtom(netSuiteBillPaymentApplyListState);

    const { currency } = useAtomValue(netSuiteBillPaymentFieldValuesState);

    const currencyPrecision = useCurrencyPrecision(currency);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const onApplyListDiscountAmountChange = useCallback(
        (id: string, value: number | null) => {
            setApplyList((current) =>
                current.map((item) => {
                    if (item.id === id) {
                        const { amountDue, paymentAmount, discountAmount } = item;

                        const preciseValue = setCurrencyPrecision(value, currencyPrecision);

                        if (!numberHelpers.isNumber(amountDue)) {
                            return item;
                        }

                        if (!numberHelpers.isNumber(paymentAmount)) {
                            return {
                                ...item,
                                discountAmount: preciseValue,
                                paymentAmount: mathService.subtract(amountDue, preciseValue || 0),
                            };
                        }

                        if (!numberHelpers.isNumber(preciseValue)) {
                            return {
                                ...item,
                                discountAmount: null,
                                paymentAmount:
                                    numberHelpers.isNumber(discountAmount) &&
                                    mathService.add(paymentAmount, discountAmount) === amountDue
                                        ? amountDue
                                        : paymentAmount,
                            };
                        }

                        if (preciseValue < 0) {
                            return {
                                ...item,
                                discountAmount: 0,
                                paymentAmount:
                                    numberHelpers.isNumber(discountAmount) &&
                                    mathService.add(paymentAmount, discountAmount) === amountDue
                                        ? amountDue
                                        : paymentAmount,
                            };
                        }

                        if (preciseValue > amountDue) {
                            return {
                                ...item,
                                discountAmount: amountDue,
                                paymentAmount: 0,
                            };
                        }

                        if (mathService.add(paymentAmount, preciseValue) > amountDue) {
                            return {
                                ...item,
                                discountAmount: preciseValue,
                                paymentAmount: mathService.subtract(amountDue, preciseValue),
                            };
                        }

                        return {
                            ...item,
                            discountAmount: preciseValue,
                            paymentAmount:
                                numberHelpers.isNumber(discountAmount) &&
                                mathService.add(paymentAmount, discountAmount) === amountDue
                                    ? mathService.subtract(amountDue, preciseValue)
                                    : paymentAmount,
                        };
                    }

                    return item;
                })
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, changeNetSuiteRequest, setApplyList]
    );

    const onApplyListPaymentAmountChange = useCallback(
        (id: string, value: number | null) => {
            setApplyList((current) =>
                current.map((item) => {
                    if (item.id === id) {
                        const preciseValue = setCurrencyPrecision(value, currencyPrecision);

                        if (!item.amountDue) {
                            return item;
                        }

                        if (!preciseValue || preciseValue < 0) {
                            return {
                                ...item,
                                paymentAmount: null,
                                discountAmount: null,
                            };
                        }

                        if (preciseValue > item.amountDue) {
                            return {
                                ...item,
                                paymentAmount: item.amountDue,
                                discountAmount: 0,
                            };
                        }

                        return {
                            ...item,
                            paymentAmount: preciseValue,
                            discountAmount:
                                mathService.add(preciseValue, item.discountAmount || 0) > item.amountDue
                                    ? mathService.subtract(item.amountDue, preciseValue)
                                    : item.discountAmount,
                        };
                    }

                    return item;
                })
            );

            changeNetSuiteRequest();
        },
        [currencyPrecision, changeNetSuiteRequest, setApplyList]
    );

    return {
        onApplyListDiscountAmountChange,
        onApplyListPaymentAmountChange,
    };
};

export const useNetSuiteBillPaymentFields = () => {
    const setFieldValues = useSetAtom(netSuiteBillPaymentFieldValuesState);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const updateFieldsBulk = useCallback(
        (data: Partial<NetSuiteBillPaymentFields>, shouldDispatchChangeEvent = true) => {
            setFieldValues((current) => {
                if (current !== null) {
                    return {
                        ...current,
                        ...data,
                    };
                }

                return current;
            });

            if (shouldDispatchChangeEvent) {
                changeNetSuiteRequest();
            }
        },
        [changeNetSuiteRequest, setFieldValues]
    );

    const onFieldChange = useCallback(
        <K extends keyof NetSuiteBillPaymentFields>(key: K, value: NetSuiteBillPaymentFields[K]) => {
            setFieldValues((current) => ({
                ...current,
                [key]: value,
            }));

            changeNetSuiteRequest();
        },
        [changeNetSuiteRequest, setFieldValues]
    );

    return {
        updateFieldsBulk,
        onFieldChange,
    };
};

export const useNetSuiteBillPaymentBillsFilters = (currencyPrecision: number) => {
    const [applyList, setApplyList] = useAtom(netSuiteBillPaymentApplyListState);
    const [selectableBills, setSelectableBills] = useAtom(netSuiteBillPaymentSelectableBillsState);

    const setFilterTransfer = useSetAtom(netSuiteBillPaymentBillsFilterTransferState);

    const checkedBillIds = useMemo(
        () => selectableBills.filter(({ isSelected }) => isSelected).map(({ id }) => id),
        [selectableBills]
    );

    const totalAmountOfSelectedBills = useMemo(
        () =>
            selectableBills.reduce(
                (total, bill) =>
                    bill.isSelected
                        ? mathService.add(total, mathService.round(bill.amountDue || 0, currencyPrecision))
                        : total,
                0
            ),
        [selectableBills, currencyPrecision]
    );

    const updateSelectableBills = useCallback(
        (bills: GetNetSuiteBillPaymentBills['response']['items'], keepPreviousBills?: boolean) => {
            setSelectableBills((previousSelectableBills) => {
                const result = keepPreviousBills ? [...previousSelectableBills] : [];

                bills.forEach((bill) => {
                    const selectedApplyListItem = applyList.find(
                        ({ transactionId }) => transactionId === bill.transactionId
                    );

                    result.push({
                        ...bill,
                        isSelected: Boolean(selectedApplyListItem),
                        id: selectedApplyListItem?.id ?? uniqueId('netSuiteBillPaymentSelectableBill-'),
                        attachments: bill.attachments
                            .map(mapBillPaymentBillAttachment)
                            .sort(
                                compareHelpers.comparatorFor<domain.RequestAttachment>(
                                    compareHelpers.stringComparator2Asc,
                                    'name'
                                )
                            ),
                    });
                });

                return result;
            });
        },
        [applyList, setSelectableBills]
    );

    const onCheckedBillsChange = useCallback(
        (ids: string[]) => {
            setSelectableBills((bills) => bills.map((bill) => ({ ...bill, isSelected: ids.includes(bill.id) })));
        },
        [setSelectableBills]
    );

    const addBills = useCallback(() => {
        setApplyList((currentApplyList) => {
            const selectableBillsIds = selectableBills.map(({ id }) => id);
            const previousApplyListItems = currentApplyList.filter(({ id }) => !selectableBillsIds.includes(id));

            return [
                ...previousApplyListItems,
                ...selectableBills
                    .filter(({ isSelected }) => isSelected)
                    .map((bill, index) => {
                        const amountDue = setCurrencyPrecision(bill.amountDue, currencyPrecision);

                        return {
                            ...bill,
                            amountDue,
                            discountAmount: 0,
                            paymentAmount: amountDue,
                            requestId: bill.requestId,
                            isEmpty: false,
                            lineNumber: index,
                            attachments: bill.attachments
                                .map(mapBillPaymentBillAttachment)
                                .sort(
                                    compareHelpers.comparatorFor<domain.RequestAttachment>(
                                        compareHelpers.stringComparator2Asc,
                                        'name'
                                    )
                                ),
                        };
                    }),
            ];
        });

        setSelectableBills([]);
        setFilterTransfer({});
    }, [selectableBills, currencyPrecision, setApplyList, setSelectableBills, setFilterTransfer]);

    return {
        checkedBillIds,
        totalAmountOfSelectedBills,
        updateSelectableBills,
        onCheckedBillsChange,
        addBills,
    };
};

export const useGetRequestTransfer = (
    request: domain.NetSuiteBillPaymentRequest,
    currency: domain.NetSuiteCurrency | null,
    templateVersion: number,
    defaultCurrencyRate: number | undefined
) => {
    const fieldValues = useAtomValue(netSuiteBillPaymentFieldValuesState);
    const applyList = useAtomValue(netSuiteBillPaymentApplyListState);
    const creditList = useAtomValue(netSuiteBillPaymentCreditListState);
    const customFieldsValues = useAtomValue(netSuiteCustomFieldsState);
    const amount = useNetSuiteBillPaymentAmount(currency);

    const getRequestTransfer = useCallback(
        (commentText?: string): backend.Transfer<backend.transfers.RequestEditTransfer> => {
            return {
                requestId: request.id,
                currency: currency?.ISOCode,
                exchangeRate: fieldValues.exchangeRate || defaultCurrencyRate || 1,
                exchangeRateSource:
                    typeof fieldValues.exchangeRate === 'number'
                        ? domain.ExchangeRateSource.Manual
                        : domain.ExchangeRateSource.NetSuite,
                exchangeRateDate: isFutureDate(fieldValues.transactionDate)
                    ? moment.utc().format('yyyy-MM-DD')
                    : moment.utc(fieldValues.transactionDate).format('yyyy-MM-DD'),
                attachments: request.attachments.map((attachment) => attachment.id),
                requestNote: request.requestNote.value,
                companyId: request.companyId,
                templateVersion,
                commentText,

                netSuiteBillPaymentOrderDetails: {
                    amount,
                    balance: null,
                    payeeId: fieldValues.payee?.id,
                    payeeType: fieldValues.payee?.type,
                    accountId: fieldValues.account?.id,
                    aPAccountId: fieldValues.payablesAccount?.id,
                    currencyId: fieldValues.currency?.id,
                    exchangeRate: fieldValues.exchangeRate,
                    toBePrinted: fieldValues.toBePrinted,
                    printVoucher: fieldValues.printVoucher,
                    checkNumber: fieldValues.checkNumber,
                    transactionDate: fieldValues.transactionDate,
                    memo: fieldValues.memo,
                    departmentId: fieldValues.department?.id,
                    classId: fieldValues.class?.id,
                    locationId: fieldValues.location?.id,
                    payeeAddressId: fieldValues.payeeAddress?.id,
                    transactionNumber: fieldValues.transactionNumber,
                    applyLines: applyList,
                    creditLines: creditList,
                    customFields: getCustomFieldsTransfer(Object.values(customFieldsValues)),
                },
            };
        },
        [
            amount,
            applyList,
            creditList,
            fieldValues,
            customFieldsValues,
            request,
            currency,
            defaultCurrencyRate,
            templateVersion,
        ]
    );

    return getRequestTransfer;
};

export const useGetValidationErrors = (params: {
    customFields: domain.Field[];
    workflowId: string;
    fieldSettings: ReturnType<typeof useNetSuiteBillPaymentFieldSettings>;
    companyCurrency: string;
    requestCurrencyISOCode: string | null;
    defaultCurrencyRate: number | undefined;
    attachmentsFieldSettings: ReturnType<typeof useNetSuiteAttachmentsFieldSettings>;
    attachments: domain.RequestAttachment[];
}) => {
    const {
        workflowId,
        companyCurrency,
        requestCurrencyISOCode,
        customFields,
        fieldSettings,
        defaultCurrencyRate,
        attachmentsFieldSettings,
        attachments,
    } = params;

    const fieldValues = useAtomValue(netSuiteBillPaymentFieldValuesState);
    const customFieldsValues = useAtomValue(netSuiteCustomFieldsState);
    const applyList = useAtomValue(netSuiteBillPaymentApplyListState);
    const creditList = useAtomValue(netSuiteBillPaymentCreditListState);
    const setShowErrors = useSetAtom(netSuiteShowErrorsState);

    const getValidationErrors = async () => {
        setShowErrors(true);

        return getBillPaymentValidationErrors({
            applyList,
            creditList,
            workflowId,
            companyCurrency,
            requestCurrencyISOCode,
            defaultCurrencyRate,
            fields: fieldValues,
            customFieldsValues: customFieldsValues,
            customFieldsList: customFields,
            billPaymentFieldSettings: fieldSettings,
            attachmentsFieldSettings,
            attachments,
        });
    };

    return getValidationErrors;
};

export const useSaveAndSubmitMutation = (
    request: domain.NetSuiteBillPaymentRequest,
    getValidationErrors: () => Promise<string[]>,
    getRequestTransfer: (commentText?: string) => backend.Transfer<backend.transfers.RequestEditTransfer>
) => {
    const setApplyListRequestIdsWithStatusError = useSetAtom(
        netSuiteBillPaymentApplyListRequestIdsWithStatusErrorState
    );
    const setApplyListRequestIdsWithPaymentError = useSetAtom(
        netSuiteBillPaymentApplyListRequestIdsWithPaymentErrorState
    );
    const commands = useSelector(getEditCommands);
    const setHasChanges = useSetAtom(hasChangesState);

    const { mutateAsync: publishRequest, isLoading: isLoadingEditRequest } = usePublishRequest();
    const { mutateAsync: editRequest, isLoading: isLoadingPublishRequest } = useEditRequestForm();
    const { mutateAsync: completeReview, isLoading: isLoadingCompleteReview } = useCompleteReview();

    const { refetch: refetchGetRequest } = useGetRequest(
        {
            query: { requestId: request.id, companyId: request.companyId },
        },
        { enabled: false }
    );

    const { mutate: editAndPublishRequest } = useMutation(
        async (params: {
            transfer: DeepNullablePartial<backend.transfers.RequestEditTransfer>;
            needToCompleteReview?: boolean;
        }) => {
            setApplyListRequestIdsWithStatusError([]);
            setApplyListRequestIdsWithPaymentError([]);
            setHasChanges(false);

            const { transfer, needToCompleteReview } = params;

            const shouldPublish = !commands.saveAndSubmit.hidden && !commands.saveAndSubmit.disabled;

            try {
                await editRequest(
                    {
                        body: transfer,
                    },
                    {
                        onSuccess: () => {
                            if (!shouldPublish && !needToCompleteReview) {
                                redirectToRequest(request);
                            }
                        },
                    }
                );

                if (needToCompleteReview) {
                    const { data } = await refetchGetRequest();

                    const requestVersion = data?.requests[0].version ?? request.version;

                    await completeReview(
                        {
                            body: {
                                requestVersion,
                                requestId: request.id,
                                companyId: request.companyId,
                            },
                        },
                        {
                            onSuccess: () => {
                                redirectToRequest(request);
                            },
                        }
                    );
                }

                if (shouldPublish) {
                    await publishRequest(
                        {
                            body: {
                                requestId: request.id,
                                companyId: request.companyId,
                            },
                        },
                        {
                            onSuccess: () => {
                                amplitudeService.sendData('requests: create request', {
                                    'request type': request.integrationCode.toLocaleLowerCase(),
                                });
                                toast.success(messages.saveAndSubmitRequestSuccess);
                                redirectToRequest(request);
                            },
                        }
                    );
                }
            } catch (error) {
                if (!errorHelpers.isApiError(error)) {
                    return;
                }

                if (error.code === ErrorCode.E4215_BILL_PAYMENT_LINE_HAS_INVALID_STATUS) {
                    const applyListRequestIdsWithStatusError = error.requestIds;

                    setApplyListRequestIdsWithStatusError(applyListRequestIdsWithStatusError || []);

                    return;
                }

                if (error.code === ErrorCode.E4214_BILL_PAYMENT_LINE_AMOUNT_EXCEEDS_AMOUNT_DUE) {
                    const applyListRequestIdsWithPaymentError = (error.invalidLines || []).reduce<string[]>(
                        (requestIds, line) => {
                            const requestId = line.RequestId ?? line.requestId;

                            if (requestId) {
                                requestIds.push(requestId);
                            }

                            return requestIds;
                        },
                        []
                    );

                    setApplyListRequestIdsWithPaymentError(applyListRequestIdsWithPaymentError);

                    return;
                }
            }
        }
    );

    const saveAndSubmit = useCallback(
        async (commentText?: string, needToCompleteReview?: boolean) => {
            const errors = await getValidationErrors();

            if (errors.length > 0) {
                errors.forEach((error) => {
                    notificationService.showErrorToast(error);
                });

                return;
            }

            const transfer = getRequestTransfer(commentText);

            editAndPublishRequest({ transfer, needToCompleteReview });
        },
        [editAndPublishRequest, getValidationErrors, getRequestTransfer]
    );

    return { saveAndSubmit, isSaving: isLoadingEditRequest || isLoadingPublishRequest || isLoadingCompleteReview };
};
